// app/javascript/vue/utils/consultationExtractedQuestionsHighlight.js
// 相談文中の「抽出疑問」に対応する範囲を下線付きでマークアップし、
// エディタ表示用のHTML文字列を生成するユーティリティ関数群を提供する。

// 注意点:
// - 入力文字列のエスケープ処理を行い、XSS攻撃を防止する。
// - 改行コード(\n, \r\n, \r)を<br>タグに変換する。
// - 抽出疑問ごとに下線付きマークアップを生成し、インデックス番号や選択状態に応じたクラスを付与する。
// - 抽出疑問の範囲が複数・重複するケースでもネストした下線を表現できるような構造を前提としている。

// 全角コロン
const FULL_WIDTH_COLON = "：";

type ExtractedQuestion = {
  id?: number | null;
  quote?: string | null;
};

type BuildAdditionalDetailsHighlightParams = {
  additionalDetails?: string;
  extractedQuestions?: ExtractedQuestion[];
  selectedQuestionId?: number | string | null;
};

type ConsultationDetails = Record<string, string | null | undefined>;

type BuildConsultationDetailsHighlightParams = {
  consultationDetails?: ConsultationDetails;
  extractedQuestions?: ExtractedQuestion[];
  selectedQuestionId?: number | string | null;
};

type DetailEntry = {
  key: string;
  sanitizedKey: string;
  html: string;
};

type CollectedMatch = {
  source: "detail" | "additional";
  candidates: string[];
  questionId?: number | null;
  detailIndex?: number;
};

type MatchPosition = {
  start: number;
  end: number;
};

type Replacement = MatchPosition & {
  isSelected: boolean;
};

type MergedReplacement = MatchPosition & {
  innerLengths: number[];
  isSelected: boolean;
};

// HTML特殊文字をエスケープする
function _escapeHtml(value: unknown): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// null/undefined を空文字にしつつHTMLエスケープする
function _sanitizeString(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  return _escapeHtml(value);
}

// 見やすさのため、文末の「。」を取り除く
function _stripTrailingJapanesePeriod(value: unknown): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.replace(/。+$/u, "");
}

// 改行コードを <br> に変換する
function _convertLineBreaks(original = ""): string {
  return original
    .replaceAll(/\r\n/g, "<br>")
    .replaceAll(/\r/g, "<br>")
    .replaceAll(/\n/g, "<br>");
}

// 下線付きマークアップを生成する
function _createUnderlineMarkup(
  text: string,
  { isSelected = false }: { isSelected?: boolean; } = {}
): string {
  const baseClass = "overlay-editor__underline overlay-editor__underline--ai";
  const className = isSelected
    ? `${baseClass} overlay-editor__underline--ai--selected`
    : baseClass;
  return `<span class="${className}"><span class="overlay-editor__underline-text">${text}</span></span>`;
}

// 正規表現のメタ文字をエスケープする
function _escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// 相談詳細を配列化し、キーと値をサニタイズする
// 表示順は入力の順序を維持する
function _buildDetailEntries(details: ConsultationDetails = {}): DetailEntry[] {
  if (!details || typeof details !== "object") {
    return [];
  }
  return Object.entries(details).map(([key, value]) => {
    const normalizedKey = key === null || key === undefined ? "" : String(key);
    const normalizedValue = value === null || value === undefined ? "" : String(value);
    const sanitizedKey = _sanitizeString(normalizedKey);
    const sanitizedValue = _sanitizeString(normalizedValue);
    return {
      key: normalizedKey,
      sanitizedKey,
      html: `${sanitizedKey}${FULL_WIDTH_COLON}${sanitizedValue}`,
    };
  });
}

// 引用文の候補文字列を生成する
function _buildQuoteCandidates(quote: unknown): string[] {
  if (typeof quote !== "string") {
    return [];
  }
  const normalized = _stripTrailingJapanesePeriod(quote);
  const trimmed = _stripTrailingJapanesePeriod(quote.trim());
  const candidates = [normalized, trimmed].filter((candidate) => candidate && candidate.length > 0);
  const uniqueCandidates = [...new Set(candidates)];
  return uniqueCandidates.map((candidate) => _sanitizeString(candidate));
}

// 対象文字列に候補文字列が含まれるか判定する
function _stringContainsAny(target: string, candidates: string[] = []): boolean {
  if (typeof target !== "string" || target.length === 0) {
    return false;
  }
  return candidates.some((candidate) => candidate && target.includes(candidate));
}

// 文字列に下線付きマークアップを適用する
// 最初に見つかった候補のみを下線にする点に注意
function _applyUnderline(
  target: string,
  candidates: string[],
  { isSelected = false }: { isSelected?: boolean } = {}
): string {
  if (typeof target !== "string" || target.length === 0) {
    return target;
  }
  for (const candidate of candidates) {
    if (!candidate) continue;
    const regex = new RegExp(_escapeRegExp(candidate));
    if (regex.test(target)) {
      return target.replace(regex, (match) =>
        _createUnderlineMarkup(match, { isSelected })
      );
    }
  }
  return target;
}

// 対象文字列から最初に一致する候補の位置を取得する
// 位置情報は後段の置換処理に利用する
function _findFirstCandidateMatch(target: string, candidates: string[] = []): MatchPosition | null {
  if (typeof target !== "string" || target.length === 0) {
    return null;
  }
  for (const candidate of candidates) {
    if (!candidate) continue;
    const start = target.indexOf(candidate);
    if (start >= 0) {
      return {
        start,
        end: start + candidate.length,
      };
    }
  }
  return null;
}

// 同一開始位置の引用に対して下線を入れ子で生成する
function _buildNestedPrefixUnderline(text: string, innerLengths: number[] = []): string {
  if (!Array.isArray(innerLengths) || innerLengths.length === 0) {
    return text;
  }

  const sortedLengths = [...innerLengths].filter((length) => length > 0).sort((a, b) => b - a);
  const buildNested = (segment: string, lengths: number[]): string => {
    if (!lengths.length) {
      return segment;
    }

    const [currentLength, ...rest] = lengths;
    if (!currentLength || currentLength > segment.length) {
      return buildNested(segment, rest);
    }

    const prefix = segment.slice(0, currentLength);
    const suffix = segment.slice(currentLength);
    const nestedPrefix = buildNested(prefix, rest);
    return _createUnderlineMarkup(nestedPrefix) + suffix;
  };

  return buildNested(text, sortedLengths);
}

// 配列から最初の一致だけを取り除く
// 外側の長さを一度だけ除外する用途
function _removeFirstOccurrence(values: number[], target: number): number[] {
  let removed = false;
  return values.filter((value) => {
    if (!removed && value === target) {
      removed = true;
      return false;
    }
    return true;
  });
}

// 抽出疑問IDの型差異（number/string）を吸収して選択状態を判定する
function _isSelectedQuestion(
  matchQuestionId: number | null | undefined,
  selectedQuestionId: number | string | null | undefined
): boolean {
  if (selectedQuestionId === undefined || selectedQuestionId === null) {
    return false;
  }
  if (matchQuestionId === undefined || matchQuestionId === null) {
    return false;
  }
  return String(matchQuestionId) === String(selectedQuestionId);
}

// 同一開始位置のマッチ群を1件の置換情報にまとめる
function _buildMergedReplacement(start: number, items: Replacement[]): MergedReplacement {
  const lengths = items.map((item) => item.end - item.start);
  const outerLength = Math.max(...lengths, 0);
  const innerLengths = _removeFirstOccurrence(lengths, outerLength);

  return {
    start,
    end: start + outerLength,
    innerLengths,
    isSelected: items.some((item) => item.isSelected),
  };
}

// 置換情報をもとに下線付きHTMLへ置き換える
// 後方から置換することでインデックスずれを防ぐ
function _applyMergedReplacements(baseHtml: string, mergedReplacements: MergedReplacement[]): string {
  const sortedReplacements = [...mergedReplacements].sort((a, b) => b.start - a.start);
  let replacedHtml = baseHtml;

  sortedReplacements.forEach((replacement) => {
    const original = baseHtml.slice(replacement.start, replacement.end);
    const innerMarkup = _buildNestedPrefixUnderline(original, replacement.innerLengths);
    const underlineMarkup = _createUnderlineMarkup(innerMarkup, {
      isSelected: replacement.isSelected,
    });
    replacedHtml =
      replacedHtml.slice(0, replacement.start) +
      underlineMarkup +
      replacedHtml.slice(replacement.end);
  });

  return replacedHtml;
}

// 詳細のキー部分を太字にする
// 既にサニタイズ済みのキーを対象にする
function _addKeyBold(html: string, sanitizedKey: string): string {
  if (!sanitizedKey || typeof html !== "string") {
    return html;
  }
  return html.replace(sanitizedKey, `<b>${sanitizedKey}</b>`);
}

// 相談詳細と相談文から一致箇所を収集する
// 引用文がどのフィールドに含まれるかを分類する
function _collectMatches(
  detailEntries: DetailEntry[],
  additionalHtml: string,
  questions: ExtractedQuestion[]
): CollectedMatch[] {
  const matches: CollectedMatch[] = [];
  if (!Array.isArray(questions) || questions.length === 0) {
    return matches;
  }

  questions.forEach((question) => {
    const candidates = _buildQuoteCandidates(question?.quote);
    if (candidates.length === 0) {
      return;
    }

    const questionId = question?.id;

    const detailIndex = detailEntries.findIndex((entry) => _stringContainsAny(entry.html, candidates));
    if (detailIndex >= 0) {
      matches.push({
        source: "detail",
        detailIndex,
        candidates,
        questionId,
      });
      return;
    }

    if (_stringContainsAny(additionalHtml, candidates)) {
      matches.push({
        source: "additional",
        candidates,
        questionId,
      });
    }
  });

  return matches;
}

// AdditionalDetailsHighlightedWithExtractedQuestions.vue から呼び出される
// 相談文（相談したいこと）を抽出された疑問でハイライトしたHTMLを生成する
// 具体的なHTML出力の例はテストコードを参考にすること
export function buildAdditionalDetailsHighlight({
  additionalDetails,
  extractedQuestions,
  selectedQuestionId,
}: BuildAdditionalDetailsHighlightParams = {}): string {
  let additionalHtml = _sanitizeString(typeof additionalDetails === "string" ? additionalDetails : "");
  const matches = _collectMatches(
    [],
    additionalHtml,
    Array.isArray(extractedQuestions) ? extractedQuestions : []
  );

  // 抽出された疑問の順に1件ずつ置換する
  matches.forEach((match) => {
    const isSelected = _isSelectedQuestion(match.questionId, selectedQuestionId);
    if (match.source === "additional") {
      additionalHtml = _applyUnderline(additionalHtml, match.candidates, { isSelected });
    }
  });

  return _convertLineBreaks(additionalHtml);
}

// ConsultationDetailsHighlightedWithExtractedQuestions.vue から呼び出される
// 相談詳細（特に、「一番気になっていること」）を抽出された疑問でハイライトしたHTMLの配列を生成する
// 具体的なHTML出力の例はテストコードを参考にすること
export function buildConsultationDetailsHighlight({
  consultationDetails,
  extractedQuestions,
  selectedQuestionId,
}: BuildConsultationDetailsHighlightParams = {}): Array<{ key: string; html: string }> {
  const detailEntries = _buildDetailEntries(consultationDetails);
  const matches = _collectMatches(
    detailEntries,
    "",
    Array.isArray(extractedQuestions) ? extractedQuestions : []
  );

  // 詳細行ごとにマッチを束ねる
  const matchesByDetailIndex = matches.reduce((map, match) => {
    if (match.source !== "detail" || typeof match.detailIndex !== "number") {
      return map;
    }
    if (!map.has(match.detailIndex)) {
      map.set(match.detailIndex, []);
    }
    const entries = map.get(match.detailIndex);
    if (entries) {
      entries.push(match);
    }
    return map;
  }, new Map<number, CollectedMatch[]>());

  return detailEntries.map((entry, index) => {
    const baseHtml = entry.html;
    const relatedMatches = matchesByDetailIndex.get(index) ?? [];

    if (relatedMatches.length === 0) {
      return {
        key: entry.key,
        html: _addKeyBold(baseHtml, entry.sanitizedKey),
      };
    }

    // 候補の先頭一致位置を取得して置換情報にする
    const replacements = relatedMatches
      .map((match): Replacement | null => {
        const position = _findFirstCandidateMatch(baseHtml, match.candidates);
        if (!position) {
          return null;
        }

        return {
          start: position.start,
          end: position.end,
          isSelected: _isSelectedQuestion(match.questionId, selectedQuestionId),
        };
      })
      .filter((replacement): replacement is Replacement => replacement !== null);

    if (replacements.length === 0) {
      return {
        key: entry.key,
        html: _addKeyBold(baseHtml, entry.sanitizedKey),
      };
    }

    // 開始位置が同じものをまとめ、ラベルを統合する
    const grouped = replacements.reduce((map, replacement) => {
      if (!map.has(replacement.start)) {
        map.set(replacement.start, []);
      }
      const entries = map.get(replacement.start);
      if (entries) {
        entries.push(replacement);
      }
      return map;
    }, new Map<number, Replacement[]>());

    // 開始位置ごとに1件へ集約する
    const mergedReplacements = [...grouped.entries()].map(([start, items]) =>
      _buildMergedReplacement(start, items)
    );

    const replacedHtml = _applyMergedReplacements(baseHtml, mergedReplacements);

    return {
      key: entry.key,
      html: _addKeyBold(replacedHtml, entry.sanitizedKey),
    };
  });
}